import styled from "styled-components";
import { useEffect, useState } from "react";
import {
  Grid,
  Stack,
  Box,
  Autocomplete,
  createFilterOptions,
  IconButton,
  FormHelperText,
  TextField as MuiTextField,
} from "@mui/material";
import TextField from "src/components/core/TextField";
import font from "src/styles/font";

import { useDialog } from "src/context/DialogContext";
import { useSnackbar } from "notistack";

import CloseIcon from "@mui/icons-material/Close";
import CameraIcon from "src/components/icon/Camera";

import MoodStyleAddDialog from "./MoodStyleAddDialog";

import { getAllRooms, getAllStyles } from "src/api/shopThisMood";

const S = {};

S.PreviewImageUploadBox = styled.div`
  position: relative;
  padding-top: 100%;
  background-color: #f3f1f2;
  border-radius: 24px;
  /* grid-column: span 7; */
`;

S.UploadInnerBox = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

S.UploadInput = styled.label`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
`;

S.UploadLabel = styled.span`
  font-size: ${font.size.md};
  font-weight: bold;
  color: #6c6c70;
  margin-top: 16px;

  &::after {
    content: "*";
    font-weight: 600;
    color: #e04132;
  }
`;

S.UploadHelperText = styled.span`
  display: flex;
  justify-content: center;
  font-size: ${font.size.sm};
  color: #6c6c70;
  margin-top: 8px;
`;

S.UploadedPreviewImage = styled.img`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  border-radius: 15px;
  object-fit: cover;
`;

const Step1 = ({ disableRoomField, onChange, formData, formErrors }) => {
  const { openDialog, closeDialog } = useDialog();
  const { enqueueSnackbar } = useSnackbar();

  const [rooms, setRooms] = useState([]);
  const [styles, setStyles] = useState([]);

  const filter = createFilterOptions();

  const fetchRooms = async () => {
    try {
      const result = await getAllRooms();
      setRooms(result);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchStyles = async () => {
    try {
      const result = await getAllStyles();
      setStyles(result);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchRooms();
    fetchStyles();
  }, []);

  return (
    <Grid container>
      <Grid item md={5}>
        <S.PreviewImageUploadBox>
          {!formData?.media?.image ? (
            <S.UploadInnerBox>
              <Stack flexDirection={"row"}>
                <input
                  id="previewImgFieldHandle"
                  type="file"
                  accept=".jpg,.jpeg,.png"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    e.preventDefault();
                    onChange(null, "media.image", e.target.files[0]);
                  }}
                  onClick={(e) => (e.currentTarget.value = null)}
                />
                <S.UploadInput htmlFor="previewImgFieldHandle">
                  <CameraIcon size={60} color="#BCBCC0" />
                  <S.UploadLabel>อัปโหลดรูปภาพ</S.UploadLabel>
                  <S.UploadHelperText>
                    รูปภาพขนาด 1240 X 1240 px ขนาดไฟล์สูงสุดไม่เกิน 4 MB
                  </S.UploadHelperText>
                </S.UploadInput>
              </Stack>
            </S.UploadInnerBox>
          ) : (
            <>
              <S.UploadedPreviewImage
                src={
                  typeof formData?.media?.image === "string"
                    ? formData?.media?.image
                    : URL.createObjectURL(formData?.media?.image)
                }
                alt=""
              />
              <Stack
                sx={{
                  position: "absolute",
                  top: 6,
                  right: 6,
                }}
                flexDirection={"row"}
                columnGap={1}
              >
                <IconButton
                  sx={{
                    padding: "4px",
                    backgroundColor: "#BCBCC0bd",
                  }}
                  onClick={() => {
                    onChange(null, `media.image`, null);
                  }}
                >
                  <CloseIcon sx={{ color: "#ffffff" }} />
                </IconButton>
              </Stack>
            </>
          )}
        </S.PreviewImageUploadBox>
        {formErrors[`media.image`] && (
          <FormHelperText error={!!formErrors[`media.image`]}>
            กรุณาเลือกรูปภาพห้อง
          </FormHelperText>
        )}
      </Grid>
      <Grid item md={7}>
        <Stack
          sx={{
            pl: 3,
          }}
        >
          <label
            style={{
              alignSelf: "flex-start",
              fontSize: font.size.md,
              marginBlock: 12,
            }}
          >
            ชื่อภาพ
          </label>
          <TextField
            label="ชื่อภาพมูด"
            variant="outlined"
            required
            value={formData?.mood_name}
            onChange={(e) => onChange(e, `mood_name`)}
            fullWidth
            inputProps={{ maxLength: 50 }}
            helperText={
              !!formErrors[`mood_name`] ? (
                "กรุณากรอกชื่อภาพมูด"
              ) : (
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  {formData?.mood_name?.length}/50
                </Box>
              )
            }
            error={!!formErrors[`mood_name`]}
          />
          <label
            style={{
              alignSelf: "flex-start",
              fontSize: font.size.md,
              marginBlock: 12,
            }}
          >
            ห้อง
          </label>
          <Autocomplete
            isOptionEqualToValue={(option, value) => option?._id === value?._id}
            renderOption={(props, option) => (
              <li {...props}>{option?.room_th}</li>
            )}
            options={rooms}
            value={formData?.room}
            onChange={(event, newValue) => {
              onChange(null, `room`, newValue);
            }}
            getOptionLabel={(option) => {
              // e.g value selected with enter, right from the input
              if (typeof option === "string") {
                return option;
              }
              if (option.inputValue) {
                return option.inputValue;
              }
              return option?.room_th;
            }}
            filterOptions={(options, params) => filter(options, params)}
            selectOnFocus
            clearOnBlur
            handleHomeEndKeys
            // freeSolo
            sx={{
              // my: 2,
              width: "100%",
              background: "#fff",
              ".MuiOutlinedInput-root": {
                paddingLeft: "16px",
              },
              ".MuiAutocomplete-option": {
                p: 3,
              },
            }}
            renderInput={(params) => (
              <MuiTextField
                className="TextField-without-border-radius"
                {...params}
                label="ห้อง"
                required
                error={!!formErrors[`room`]}
                helperText={!!formErrors[`room`] ? "กรุณาเลือกห้อง" : ""}
              />
            )}
            disabled={disableRoomField}
          />
          <label
            style={{
              alignSelf: "flex-start",
              fontSize: font.size.md,
              marginBlock: 12,
            }}
          >
            สไตล์
          </label>
          <Autocomplete
            multiple
            isOptionEqualToValue={(option, value) =>
              option?.name === value?.name
            }
            renderOption={(props, option) => <li {...props}>{option.name}</li>}
            options={styles}
            value={formData?.styles}
            onChange={(event, newValue) => {
              const lastAdded = newValue[newValue.length - 1];
              if (lastAdded?.inputValue) {
                openDialog({
                  type: "custom",
                  customDialog: (
                    <MoodStyleAddDialog
                      initialValue={lastAdded.inputValue}
                      onSubmit={(val) => {
                        let existHashtag = newValue.find(
                          (tag) =>
                            tag?.name?.toLowerCase() ===
                            val?.trim()?.toLowerCase(),
                        );

                        if (existHashtag) {
                          enqueueSnackbar(`This style is already exist!`, {
                            variant: "error",
                          });
                          return;
                        }

                        newValue.pop(); // remove the last added item before adding the new one
                        onChange(null, `styles`, [...newValue, val?.trim()]);
                        closeDialog();
                      }}
                      onClose={() => {
                        closeDialog();
                      }}
                    />
                  ),
                });
              } else {
                onChange(null, `styles`, newValue);
              }
            }}
            getOptionLabel={(option) => {
              // e.g value selected with enter, right from the input
              if (typeof option === "string") {
                return option;
              }
              if (option.inputValue) {
                return option.inputValue;
              }
              return option?.name;
            }}
            filterOptions={(options, params) => {
              const filtered = filter(options, params);

              if (params.inputValue !== "") {
                filtered.push({
                  inputValue: params.inputValue,
                  name: `Add "${params.inputValue}"`,
                });
              }

              return filtered;
            }}
            selectOnFocus
            clearOnBlur
            handleHomeEndKeys
            // freeSolo
            sx={{
              // my: 2,
              width: "100%",
              background: "#fff",
              ".MuiOutlinedInput-root": {
                paddingLeft: "16px",
              },
              ".MuiAutocomplete-option": {
                p: 3,
              },
            }}
            renderInput={(params) => (
              <MuiTextField
                className="TextField-without-border-radius"
                {...params}
                label="สไตล์"
                required
                error={!!formErrors[`styles`]}
                helperText={
                  !!formErrors[`styles`]
                    ? "กรุณาเลือกสไตล์"
                    : "เลือกได้ไม่เกิน 5 สไตล์"
                }
              />
            )}
          />
        </Stack>
      </Grid>
    </Grid>
  );
};
export default Step1;
